import { useCallback, useEffect, useMemo, useState } from 'react';

import { DataStatusBanner } from '@/components/DataStatusBanner';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { MarketHeader } from '@/features/market-overview/MarketHeader';
import { MarketOverview } from '@/features/market-overview/MarketOverview';
import { MarketPulse } from '@/features/market-overview/MarketPulse';
import { TechnicalAnalytics } from '@/features/market-overview/TechnicalAnalytics';
import { NewsSection } from '@/features/news/NewsSection';
import {
  boardFilters,
  initialOpportunityFilters,
  type OpportunityFilterState,
} from '@/features/opportunities/opportunityFilterModel';
import { OpportunityRadar } from '@/features/opportunities/OpportunityRadar';
import { TopOpportunities } from '@/features/opportunities/TopOpportunities';
import { StockDetailDrawer } from '@/features/stock-details/StockDetailDrawer';
import { useMarketData } from '@/hooks/useMarketData';
import type { EnrichedOpportunity } from '@/models/marketData';
import { enrichOpportunity } from '@/utils/marketCalculations';

export const DashboardPage = () => {
  const marketData = useMarketData();
  const [filters, setFilters] = useState<OpportunityFilterState>(initialOpportunityFilters);
  const [activeBoard, setActiveBoard] = useState<string | null>(null);
  const changeFilters = useCallback((next: OpportunityFilterState) => {
    setFilters(next);
    setActiveBoard(null);
  }, []);
  const navigateBoard = useCallback((label: string) => {
    setFilters({ ...initialOpportunityFilters, ...boardFilters[label] });
    setActiveBoard(label);
    const radar = document.getElementById('opportunity-radar');
    radar?.focus({ preventScroll: true });
    radar?.scrollIntoView?.({
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
      block: 'start',
    });
  }, []);
  const [selectedInstrumentKey, setSelectedInstrumentKey] = useState<string | null>(null);
  const opportunities = useMemo(
    () => marketData.data?.data.results.map(enrichOpportunity) ?? [],
    [marketData.data?.data.results],
  );
  const selectOpportunity = useCallback(
    (opportunity: EnrichedOpportunity) =>
      setSelectedInstrumentKey(opportunity.tradeInstrument.instrumentKey),
    [],
  );
  const closeDrawer = useCallback(() => setSelectedInstrumentKey(null), []);
  useEffect(() => {
    if (
      marketData.data &&
      selectedInstrumentKey &&
      !opportunities.some((item) => item.tradeInstrument.instrumentKey === selectedInstrumentKey)
    )
      setSelectedInstrumentKey(null);
  }, [marketData.data, opportunities, selectedInstrumentKey]);

  if (marketData.isLoading) return <LoadingSkeleton />;

  if (!marketData.data) {
    return (
      <main className="fatal-state">
        <div className="fatal-state__mark">!</div>
        <h1>Market intelligence is unavailable</h1>
        <p>{marketData.error?.message ?? 'The market data feed could not be loaded.'}</p>
        <button type="button" onClick={() => void marketData.refetch()}>
          Try again
        </button>
      </main>
    );
  }

  const latestEvaluation = opportunities.reduce(
    (latest, item) => (item.evaluatedAtUtc > latest ? item.evaluatedAtUtc : latest),
    '',
  );

  return (
    <main className="app-shell">
      <MarketHeader
        connectionStatus={marketData.connectionStatus}
        lastUpdated={marketData.lastUpdated ?? latestEvaluation}
        lastSuccessfulSync={marketData.dataUpdatedAt}
        updateNotice={marketData.updateNotice}
      />
      {marketData.isError && (
        <DataStatusBanner
          message={marketData.error.message}
          onRetry={() => void marketData.refetch()}
        />
      )}
      <div className="research-notice">
        Research intelligence <span>•</span> Non-advisory <span>•</span> For informational use only
      </div>
      <section className="command-strip" id="market-overview">
        <div className="command-intro">
          <span className="eyebrow">Momentum command center</span>
          <h2>Market signals, ranked for research.</h2>
          <p className="hero-description">Deeper context. A more informed view.</p>
        </div>
        <MarketPulse
          opportunities={opportunities}
          activeBoard={activeBoard}
          onNavigate={navigateBoard}
        />
      </section>
      <MarketOverview
        opportunities={opportunities}
        version={marketData.data.version}
        activeBoard={activeBoard}
        onNavigate={navigateBoard}
      />
      <div className="insight-layout">
        <TopOpportunities opportunities={opportunities} onSelect={selectOpportunity} />
        <TechnicalAnalytics opportunities={opportunities} />
      </div>
      <OpportunityRadar
        filters={filters}
        onFiltersChange={changeFilters}
        activeBoard={activeBoard}
        opportunities={opportunities}
        version={marketData.data.version}
        onSelect={selectOpportunity}
      />
      <NewsSection opportunities={opportunities} />
      <p className="disclaimer">
        {marketData.data.data.disclaimer ||
          'Important Disclaimer: TradeXRadar is an analytics and market-research dashboard. Information shown is not investment advice or a recommendation to buy or sell securities. Markets involve risk; independently verify information and consult an appropriately registered financial adviser where required.'}
      </p>
      <StockDetailDrawer
        opportunity={
          opportunities.find(
            ({ tradeInstrument }) => tradeInstrument.instrumentKey === selectedInstrumentKey,
          ) ?? null
        }
        onClose={closeDrawer}
      />
    </main>
  );
};
